// LearnCore Frontend - 奖励管理列表组件

import React, { useState, useEffect } from 'react';
import { rewardsApi } from '../api/tasks';
import type { RewardTemplate } from '../types/tasks-rewards';

interface RewardManagementListProps {
  onEdit?: (reward: RewardTemplate) => void;
  onDelete?: (reward: RewardTemplate) => void;
  className?: string;
}

export const RewardManagementList: React.FC<RewardManagementListProps> = ({
  onEdit,
  onDelete,
  className = ''
}) => {
  const [rewards, setRewards] = useState<RewardTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [page, setPage] = useState(0);
  const [filters, setFilters] = useState({
    is_active: undefined as boolean | undefined,
    type: undefined as string[] | undefined
  });

  useEffect(() => {
    loadRewards(true);
  }, [filters]);

  const loadRewards = async (reset = false) => {
    try {
      setLoading(true);
      setError(null);

      const request: any = {
        limit: 20,
        offset: reset ? 0 : page * 20
      };

      if (filters.is_active !== undefined) {
        request.is_active = filters.is_active.toString();
      }

      if (filters.type && filters.type.length > 0) {
        request.type = filters.type;
      }

      const response = await rewardsApi.getMyRewards(request);

      if (reset) {
        setRewards(response.rewards || []);
        setPage(0);
      } else {
        setRewards(prev => [...prev, ...(response.rewards || [])]);
      }

      setHasMore(response.has_more);
    } catch (err: any) {
      setError(err.response?.data?.error || '加载奖励列表失败');
      console.error('Failed to load rewards:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (reward: RewardTemplate) => {
    if (!confirm(`确定要删除奖励"${reward.title}"吗？此操作不可恢复。`)) {
      return;
    }

    try {
      await rewardsApi.deleteReward(reward.id);
      setRewards(prev => prev.filter(r => r.id !== reward.id));

      if (onDelete) {
        onDelete(reward);
      }
    } catch (err: any) {
      alert(err.response?.data?.error || '删除奖励失败');
    }
  };

  const handleToggleStatus = async (reward: RewardTemplate) => {
    try {
      const newStatus = !reward.is_active;
      await rewardsApi.updateReward(reward.id, { is_active: newStatus });

      setRewards(prev => prev.map(r =>
        r.id === reward.id ? { ...r, is_active: newStatus } : r
      ));
    } catch (err: any) {
      alert(err.response?.data?.error || '更新奖励状态失败');
    }
  };

  const loadMore = () => {
    if (!loading && hasMore) {
      setPage(prev => prev + 1);
      loadRewards(false);
    }
  };

  const getTypeText = (type: string): string => {
    switch (type) {
      case 'digital': return '数字奖励';
      case 'privilege': return '特权奖励';
      case 'custom': return '自定义奖励';
      default: return type;
    }
  };

  const getTypeIcon = (type: string): string => {
    switch (type) {
      case 'digital': return '🎮';
      case 'privilege': return '👑';
      case 'custom': return '🎁';
      default: return '🏆';
    }
  };

  const formatDate = (timestamp: number): string => {
    return new Date(timestamp * 1000).toLocaleDateString('zh-CN');
  };

  const handleFilterChange = (filterType: 'is_active' | 'type', value: any) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
  };

  if (loading && rewards.length === 0) {
    return (
      <div className={`space-y-4 ${className}`}>
        {[...Array(3)].map((_, i) => (
          <div key={i} className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <div className="animate-pulse space-y-4">
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <div className="h-5 bg-gray-200 dark:bg-gray-700 rounded w-3/4 mb-2"></div>
                  <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/2"></div>
                </div>
                <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-20"></div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded"></div>
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded"></div>
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded"></div>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className={className}>
      {/* 筛选器 */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4 mb-6">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">状态筛选</label>
            <select
              value={filters.is_active === undefined ? '' : filters.is_active.toString()}
              onChange={(e) => handleFilterChange('is_active', e.target.value === '' ? undefined : e.target.value === 'true')}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            >
              <option value="">全部状态</option>
              <option value="true">启用</option>
              <option value="false">禁用</option>
            </select>
          </div>
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">类型筛选</label>
            <select
              value={filters.type?.join(',') || ''}
              onChange={(e) => handleFilterChange('type', e.target.value ? e.target.value.split(',') : undefined)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            >
              <option value="">全部类型</option>
              <option value="digital">数字奖励</option>
              <option value="privilege">特权奖励</option>
              <option value="custom">自定义奖励</option>
            </select>
          </div>
          <div className="flex items-end">
            <button
              onClick={() => loadRewards(true)}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              刷新
            </button>
          </div>
        </div>
      </div>

      {/* 错误信息 */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-6">
          <div className="text-red-800 dark:text-red-400">{error}</div>
          <button
            onClick={() => loadRewards(true)}
            className="mt-2 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors text-sm"
          >
            重试
          </button>
        </div>
      )}

      {/* 奖励列表 */}
      {rewards.length === 0 && !loading ? (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-8 text-center">
          <div className="text-gray-400 dark:text-gray-500 mb-4">
            <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7" />
            </svg>
          </div>
          <div className="text-gray-600 dark:text-gray-400 font-medium">暂无自定义奖励</div>
          <div className="text-gray-500 dark:text-gray-500 text-sm mt-1">
            {filters.is_active !== undefined || filters.type ? '尝试调整筛选条件' : '创建第一个自定义奖励吧！'}
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          {rewards.map((reward) => (
            <div
              key={reward.id}
              className={`bg-white dark:bg-gray-800 rounded-lg shadow-sm border ${
                !reward.is_active ? 'border-gray-300 dark:border-gray-600 opacity-60' : 'border-gray-200 dark:border-gray-700'
              } overflow-hidden hover:shadow-md transition-shadow`}
            >
              <div className="p-6">
                {/* 奖励头部 */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <span className="text-2xl">{getTypeIcon(reward.type)}</span>
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                        {reward.title}
                      </h3>
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        reward.is_active
                          ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                          : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                      }`}>
                        {reward.is_active ? '启用中' : '已禁用'}
                      </span>
                    </div>
                    <div className="flex items-center gap-4 text-sm text-gray-600 dark:text-gray-400">
                      <span>{getTypeText(reward.type)}</span>
                      <span>•</span>
                      <span>创建于 {formatDate(reward.created_at)}</span>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-bold text-orange-600 dark:text-orange-400">
                      {reward.dkp_cost}
                    </div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">DKP</div>
                  </div>
                </div>

                {/* 奖励描述 */}
                {reward.description && (
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                    {reward.description}
                  </p>
                )}

                {/* 奖励属性 */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                  <div className="text-center p-2 bg-gray-50 dark:bg-gray-700 rounded">
                    <div className="text-lg font-medium text-gray-900 dark:text-gray-100">
                      {(reward as any).redemption_stats?.total_redemptions || 0}
                    </div>
                    <div className="text-xs text-gray-600 dark:text-gray-400">总兑换次数</div>
                  </div>
                  <div className="text-center p-2 bg-gray-50 dark:bg-gray-700 rounded">
                    <div className="text-lg font-medium text-blue-600 dark:text-blue-400">
                      {(reward as any).redemption_stats?.pending_count || 0}
                    </div>
                    <div className="text-xs text-gray-600 dark:text-gray-400">待审核</div>
                  </div>
                  <div className="text-center p-2 bg-gray-50 dark:bg-gray-700 rounded">
                    <div className="text-lg font-medium text-green-600 dark:text-green-400">
                      {(reward as any).redemption_stats?.approved_count || 0}
                    </div>
                    <div className="text-xs text-gray-600 dark:text-gray-400">已批准</div>
                  </div>
                  <div className="text-center p-2 bg-gray-50 dark:bg-gray-700 rounded">
                    <div className="text-lg font-medium text-purple-600 dark:text-purple-400">
                      {reward.max_redemptions_per_day || 1}
                    </div>
                    <div className="text-xs text-gray-600 dark:text-gray-400">每日限制</div>
                  </div>
                </div>

                {/* 操作按钮 */}
                <div className="flex gap-2 pt-4 border-t border-gray-200 dark:border-gray-700">
                  <button
                    onClick={() => handleToggleStatus(reward)}
                    className={`px-3 py-2 text-sm rounded-lg transition-colors ${
                      reward.is_active
                        ? 'bg-orange-100 text-orange-700 hover:bg-orange-200 dark:bg-orange-900/30 dark:text-orange-400'
                        : 'bg-green-100 text-green-700 hover:bg-green-200 dark:bg-green-900/30 dark:text-green-400'
                    }`}
                  >
                    {reward.is_active ? '禁用' : '启用'}
                  </button>
                  <button
                    onClick={() => onEdit?.(reward)}
                    className="px-3 py-2 text-sm bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors dark:bg-blue-900/30 dark:text-blue-400"
                  >
                    编辑
                  </button>
                  <button
                    onClick={() => handleDelete(reward)}
                    className="px-3 py-2 text-sm bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors dark:bg-red-900/30 dark:text-red-400"
                  >
                    删除
                  </button>
                </div>
              </div>
            </div>
          ))}

          {/* 加载更多 */}
          {hasMore && (
            <div className="text-center pt-4">
              <button
                onClick={loadMore}
                disabled={loading}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 transition-colors"
              >
                {loading ? '加载中...' : '加载更多'}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default RewardManagementList;